import {
  type V2MobileAccessReleaseDto,
  type V2MobileAccessStatusDto,
  v2AdminNegotiationsControllerMobileAccess,
  v2AdminNegotiationsControllerMobileAccessStatus
} from '@idc-forms/api-client'
import { V2MobileAccessStatusSchema } from '@idc-forms/schemas/api-v2/admin-negotiations'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Acceso móvil de una negociación (ADR 0024), contra la API v2.
 *
 * Dos llamadas a la MISMA ruta y una sola acción detrás: el `GET` cuenta cómo quedó el
 * acceso y el `POST` lo libera. Se direccionan por `neg_`, nunca por el `_id` de Mongo con
 * el que trabaja el resto de esta pantalla — la v2 lo rechaza en el pipe.
 *
 * Ojo con el nombre del símbolo generado: sale de la CLASE del controller
 * (`V2AdminNegotiationsController`) y no de la ruta, así que buscar «mobileAccess» por
 * ruta en el SDK devuelve cero y se lee como un endpoint que falta. Ya costó tiempo una vez.
 */

export type MobileAccessStatus = V2MobileAccessStatusDto
export type MobileAccessRelease = V2MobileAccessReleaseDto
export type MobileAccessOwner = MobileAccessStatus['owners'][number]

export const mobileAccessV2Service = {
  status: async (accessToken: string, negotiationId: string): Promise<MobileAccessStatus> => {
    const result = await v2AdminNegotiationsControllerMobileAccessStatus({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id: negotiationId }
    })
    return assertShape(V2MobileAccessStatusSchema, result.data)
  },

  /**
   * Libera el acceso. Sin `ownerId` alcanza a quien diga la política —hoy sólo al
   * propietario principal—; con `ownerId` habilita a esa persona y **la política no corre**,
   * que es el «por si lo solicitan» de la decisión Nº 28.
   *
   * El cuerpo se manda sólo cuando hay a quién nombrar. La corrida automática va sin cuerpo
   * —es su forma normal— y por eso el contrato lo declara opcional.
   */
  release: async (
    accessToken: string,
    negotiationId: string,
    ownerId?: string
  ): Promise<MobileAccessRelease> => {
    const result = await v2AdminNegotiationsControllerMobileAccess({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id: negotiationId },
      ...(ownerId ? { body: { ownerId } } : {})
    })
    return result.data
  }
}
